import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GitBranch, Handshake, Banknote,
  Bell, BarChart3, PhoneCall, LogOut, Menu, X, Building2, ChevronDown
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ForceChangePassword from "./ForceChangePassword";
import "./os.css";

const NAV = [
  { to: "/os",            end: true, label: "Dashboard",   icon: LayoutDashboard, testid: "os-nav-dashboard" },
  { to: "/os/leads",      label: "Leads",                  icon: Users,           testid: "os-nav-leads" },
  { to: "/os/pipeline",   label: "Pipeline",               icon: GitBranch,       testid: "os-nav-pipeline" },
  { to: "/os/connectors", label: "Connectors",             icon: Handshake,       testid: "os-nav-connectors" },
  { to: "/os/loans",      label: "Loans",                  icon: Banknote,        testid: "os-nav-loans" },
  { to: "/os/followups",  label: "Follow-ups",             icon: Bell,            testid: "os-nav-followups" },
  { to: "/os/calls",      label: "Calls",                  icon: PhoneCall,       testid: "os-nav-calls" },
  { to: "/os/analytics",  label: "Analytics",              icon: BarChart3,       testid: "os-nav-analytics" },
  { to: "/os/team",       label: "Team",                   icon: Building2,       testid: "os-nav-team",  roles: ["admin","manager"] },
];

export default function OSLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/os/login", { replace: true }); };

  // Force password rotation for any team member flagged (e.g., freshly-seeded admin).
  if (user?.must_change_password) return <ForceChangePassword />;

  const items = NAV.filter(n => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="os-app" data-testid="os-app-shell">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b" style={{background: "var(--os-card)", borderColor: "var(--os-border)"}}>
        <Link to="/os" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-xs" style={{background: "var(--os-accent)"}}>OS</div>
          <div className="font-bold text-sm">Our First Realty OS</div>
        </Link>
        <button onClick={() => setOpen(true)} data-testid="os-menu-open" className="p-2 rounded-md hover:bg-white/5">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="os-mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 flex flex-col shadow-xl" style={{background: "var(--os-card)"}}>
            <div className="p-4 flex items-center justify-between border-b" style={{borderColor:"var(--os-border)"}}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-xs" style={{background: "var(--os-accent)"}}>OS</div>
                <div>
                  <div className="text-sm font-bold">Our First Realty OS</div>
                  <div className="text-[10px] uppercase tracking-widest" style={{color:"var(--os-text-dim)"}}>{user?.role}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-2 flex-1 overflow-y-auto">
              {items.map(n => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  data-testid={n.testid}
                  className={({isActive}) => `os-nav-item ${isActive ? "active" : ""}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t" style={{borderColor:"var(--os-border)"}}>
              <button onClick={doLogout} className="os-btn-ghost w-full justify-center" data-testid="os-logout">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="md:grid md:grid-cols-[240px_1fr] md:min-h-screen">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col border-r" style={{background: "var(--os-card)", borderColor:"var(--os-border)"}}>
          <div className="p-5 border-b" style={{borderColor:"var(--os-border)"}}>
            <Link to="/os" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold" style={{background: "var(--os-accent)"}}>OS</div>
              <div>
                <div className="text-sm font-bold">Our First Realty OS</div>
                <div className="text-[10px] uppercase tracking-[0.15em]" style={{color: "var(--os-text-dim)"}}>Revenue OS</div>
              </div>
            </Link>
          </div>
          <nav className="py-3 flex-1 overflow-y-auto">
            {items.map(n => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({isActive}) => `os-nav-item ${isActive ? "active" : ""}`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t" style={{borderColor:"var(--os-border)"}}>
            <div className="px-2 pb-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{background:"var(--os-accent)", color:"white"}}>
                {(user?.name || user?.email || "U").slice(0,1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-widest" style={{color:"var(--os-text-dim)"}}>{user?.role}</div>
              </div>
            </div>
            <button onClick={doLogout} className="os-btn-ghost w-full justify-center" data-testid="os-logout-desktop">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </aside>

        <main className="p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
